"use client";

import { useFirebaseAuth } from "@/app/_api/firebase";
import { getSoundboard, listMySoundboards } from "@/app/_api/soundboards";
import { IS_LOCAL } from "@/env";
import { Sheet, SheetContent } from "@ui/components/core/Sheet";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";

import { AddRowButton } from "./components/AddRowButton";
import { AudioPlayer } from "./components/AudioPlayer";
import { ButtonGrid } from "./components/ButtonGrid";
import { CreateAccountCta } from "./components/CreateAccountCta";
import { PlayerPanel } from "./components/Display";
import { EditModeButton } from "./components/EditModeButton";
import { InfinitiSamplerEmblem } from "./components/InfinitiSamplerEmblem";
import { LoopButton } from "./components/LoopButton";
import { useMidiConfig } from "./components/MidiConfigurator";
import { PresetCategorySelector } from "./components/PresetCategorySelector";
import { PresetEditor } from "./components/PresetEditor";
import { NewPresetButton, PresetsRow } from "./components/PresetsRow";
import { ResetButton } from "./components/ResetButton";
import { SoundBoardLogo } from "./components/SoundBoardLogo";
import { SoundEffectEditor } from "./components/SoundEffectEditor";
import { VolumePanel } from "./components/VolumePanel";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import "./sound-board.css";
import {
  PanelType,
  closePanel,
  loadCustomSoundboardSuccess,
  loadMySoundboardsStart,
  loadMySoundboardsSuccess,
  loadPresets,
  loadSavedStateFromSessionCookie,
  updateMidiMapping,
  userStateChanged,
} from "./state/actions";
import { SoundboardProvider, useSoundboard } from "./state/reducer";
import { type ApiPreset, PresetCategory } from "./types";
const LocalDevSignUpButton = dynamic(() => import("./components/LocalSignupButton"), { ssr: false });

const MobileSoundBoard = () => {
  const { state, dispatch } = useSoundboard();
  return (
    <div className='flex w-full flex-col gap-4'>
      <SoundBoardLogo className='mb-24' />

      <PlayerPanel />
      <div className='grid grid-cols-4 gap-4'>
        <div className='grid-col-span-2 items-center'>
          <InfinitiSamplerEmblem horizontal />
        </div>
        <PresetCategorySelector category={PresetCategory.DEFAULTS} full />
        <PresetCategorySelector category={PresetCategory.OWN} full />
      </div>
      <PresetsRow />
      <div className='grid grid-cols-4 gap-4'>
        <NewPresetButton />
        <EditModeButton />
        <LoopButton full />
        <ResetButton full />
      </div>
      <ButtonGrid />
      <div className='grid grid-cols-4 gap-4'>
        <AddRowButton className='mt-auto' />
      </div>
      <Sheet onOpenChange={isOpen => !isOpen && dispatch(closePanel())} open={!!state.panel}>
        <SheetContent className='min-h-240' effect='bottom' hideCloseButton>
          <SidePanels border={false} />
        </SheetContent>
      </Sheet>
    </div>
  );
};

const SidePanels = ({ border = true }: { border?: boolean }) => {
  const { state } = useSoundboard();
  return (
    <>
      {state.panel?.type === PanelType.PRESET_EDITOR && <PresetEditor border={border} />}
      {state.panel?.type === PanelType.SOUND_EFFECT_EDITOR && <SoundEffectEditor border={border} />}
    </>
  );
};

const DesktopSoundBoard = () => {
  const { state } = useSoundboard();
  const isRunningLocally = IS_LOCAL;

  return (
    <div className='relative mx-auto w-full'>
      {isRunningLocally && <LocalDevSignUpButton />}
      <div
        className='flex gap-24 transition-transform duration-300 ease-out'
        style={{
          transform: state.panel ? "translateX(-300px)" : "translateX(0)",
        }}
      >
        <div className='hidden shrink-0 basis-1/6 flex-col justify-between lg:flex'>
          <SoundBoardLogo />
          <CreateAccountCta />
        </div>

        <div className='soundboard mx-auto flex max-w-xl shrink-0 flex-col gap-4 rounded-lg p-4 md:basis-full lg:basis-4/6'>
          <div className='grid grid-cols-5 gap-4'>
            <div className='grid-col-span-4'>
              <PlayerPanel />
            </div>
            <div className='grid-col-span-1'>
              <VolumePanel />
            </div>
          </div>
          <div className='grid grid-cols-5 gap-4'>
            <div className='grid-col-span-2'>
              <InfinitiSamplerEmblem horizontal />
            </div>
            <PresetCategorySelector category={PresetCategory.DEFAULTS} />
            <PresetCategorySelector category={PresetCategory.OWN} />
            <NewPresetButton flat />
          </div>
          <div className='grid grid-cols-5 gap-4'>
            <div className='grid-col-span-4'>
              <PresetsRow />
            </div>
            <div className='grid-col-span-1'>
              <EditModeButton />
            </div>
          </div>
          <div className='grid grid-cols-5 gap-4'>
            <div className='grid-col-span-4'>
              <ButtonGrid />
            </div>
            <div className='grid-col-span-1 flex flex-col gap-4'>
              <div className='flex aspect-1x1 flex-col gap-4'>
                <LoopButton />
                <ResetButton />
              </div>
              <AddRowButton className='mt-auto' />
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div className='hidden shrink-0 basis-1/6 lg:block' />
      </div>
      {/* Animated side panel */}
      <div
        className='absolute right-0 top-0 flex h-full w-[400px] basis-2/6 transition-all duration-300 ease-out'
        style={{
          opacity: state.panel ? 1 : 0,
          transform: state.panel ? "translateX(0)" : "translateX(100%)",
          pointerEvents: state.panel ? "auto" : "none",
        }}
      >
        <SidePanels />
      </div>
    </div>
  );
};

// Wrapper component to initialize presets
const SoundboardInitializer = (props: { params: URLSearchParams; presets: ApiPreset[] }) => {
  const { dispatch, state } = useSoundboard();
  useMidiConfig();
  const params = props.params;
  const isMobile = useIsMobile();
  const { user, loading } = useFirebaseAuth();

  const activePreset = useMemo(() => {
    const activePreset = state.presets[state.activePresetId!];
    return activePreset;
  }, [state.presets, state.activePresetId]);

  // Load presets when component mounts
  useEffect(() => {
    const serializedState = safeLocalStorage.getItem(LocalStorageKeys.SOUNDBOARD_STATE);
    if (serializedState != null && serializedState !== "undefined") {
      dispatch(loadSavedStateFromSessionCookie({ state: JSON.parse(serializedState) }));
      safeLocalStorage.removeItem(LocalStorageKeys.SOUNDBOARD_STATE);
    } else {
      dispatch(loadPresets(props.presets));
    }

    // Load MIDI mapping from local storage
    const serializedMapping = safeLocalStorage.getItem(LocalStorageKeys.SOUNDBOARD_MIDI_MAPPING);
    if (serializedMapping != null && serializedMapping !== "undefined") {
      dispatch(updateMidiMapping({ mapping: JSON.parse(serializedMapping) }));
    }
  }, [dispatch, props.presets]);

  // Sync user state from Firebase Auth
  useEffect(() => {
    if (!loading) {
      dispatch(userStateChanged({ user }));
    }
  }, [dispatch, user, loading]);

  // Load a soundboard if there is an ID in the URL
  useEffect(() => {
    const soundboardId = params.get("soundboardId");
    if (soundboardId) {
      (user?.getIdToken() || Promise.resolve(undefined)).then(token => {
        getSoundboard(soundboardId, token).then(soundboard => {
          dispatch(loadCustomSoundboardSuccess({ soundboard }));
        });
      });
    }
  }, [dispatch, props.presets, params, user]);

  // Load the user's stored presets
  useEffect(() => {
    if (user) {
      dispatch(loadMySoundboardsStart());
      user.getIdToken().then(token => {
        listMySoundboards(token).then(soundboards => {
          dispatch(loadMySoundboardsSuccess({ soundboards }));
        });
      });
    }
  }, [dispatch, user]);

  // Load the active preset sounds when the active preset changes
  useEffect(() => {
    const activePreset = state.presets[state.activePresetId!];

    if (activePreset && !activePreset?.loaded) {
      user
        ?.getIdToken()
        .then(token =>
          getSoundboard(activePreset.id, token).then(board =>
            dispatch(loadCustomSoundboardSuccess({ soundboard: board })),
          ),
        );
    }
  }, [activePreset, dispatch, state.activePresetId, state.presets, user]);

  useEffect(() => {
    // Store MIDI mapping in local storage
    safeLocalStorage.setItem(LocalStorageKeys.SOUNDBOARD_MIDI_MAPPING, JSON.stringify(state.midi.mapping));
  }, [state.midi.mapping]);

  return <>{isMobile ? <MobileSoundBoard /> : <DesktopSoundBoard />}</>;
};

// Main component that sets up the provider and initializes the soundboard
export const Soundboard = (props: { params: URLSearchParams; presets: ApiPreset[] }) => {
  return (
    <SoundboardProvider>
      <AudioPlayer>
        <div className='w-full overflow-x-hidden p-8'>
          <div className='mx-auto max-w-4xl'>
            <SoundboardInitializer params={props.params} presets={props.presets} />
          </div>
        </div>
      </AudioPlayer>
    </SoundboardProvider>
  );
};

export const SoundboardWithNextParams = (props: { presets: ApiPreset[] }) => {
  const params = useSearchParams();
  return <Soundboard params={params} presets={props.presets} />;
};
